'use client';

import { InputHTMLAttributes, forwardRef, ReactNode } from 'react';
import { motion } from 'framer-motion';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: ReactNode;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, icon, hint, className = '', placeholder, ...props }, ref) => {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-1.5"
      >
        {label && (
          <label className="flex items-center gap-1 text-xs font-bold text-gray-700 uppercase tracking-wide">
            {label}
            {props.required && <span className="text-red-500">*</span>}
          </label>
        )}
        <div className="relative group">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-500 transition-colors">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            placeholder={placeholder}
            className={`
              w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm
              placeholder:text-gray-400 text-gray-900
              focus:outline-none focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10
              transition-all duration-200
              disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
              hover:border-gray-300
              ${icon ? 'pl-10' : ''}
              ${error ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-500/10' : ''}
              ${className}
            `}
            {...props}
          />
        </div>
        {hint && !error && (
          <p className="text-xs text-gray-400">{hint}</p>
        )}
        {error && (
          <motion.p 
            initial={{ opacity: 0, y: -3 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs text-red-600 font-medium"
          >
            {error}
          </motion.p>
        )}
      </motion.div>
    );
  }
);

Input.displayName = 'Input';

interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, children, className = '', ...props }, ref) => {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-1.5"
      >
        {label && (
          <label className="flex items-center gap-1 text-xs font-bold text-gray-700 uppercase tracking-wide">
            {label}
          </label>
        )}
        <select
          ref={ref}
          className={`
            w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm
            text-gray-900 cursor-pointer
            focus:outline-none focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10
            transition-all duration-200
            hover:border-gray-300
            ${error ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-500/10' : ''}
            ${className}
          `}
          {...props}
        >
          {children}
        </select>
        {hint && !error && (
          <p className="text-xs text-gray-400">{hint}</p>
        )}
        {error && (
          <motion.p 
            initial={{ opacity: 0, y: -3 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs text-red-600 font-medium"
          >
            {error}
          </motion.p>
        )}
      </motion.div>
    );
  }
);

Select.displayName = 'Select';

interface TextareaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  rows?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, rows = 4, className = '', ...props }, ref) => {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-1.5"
      >
        {label && (
          <label className="flex items-center gap-1 text-xs font-bold text-gray-700 uppercase tracking-wide">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          rows={rows}
          className={`
            w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm
            placeholder:text-gray-400 text-gray-900
            focus:outline-none focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10
            transition-all duration-200 resize-y
            hover:border-gray-300
            ${error ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-500/10' : ''}
            ${className}
          `}
          {...props}
        />
        {hint && !error && (
          <p className="text-xs text-gray-400">{hint}</p>
        )}
        {error && (
          <motion.p 
            initial={{ opacity: 0, y: -3 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs text-red-600 font-medium"
          >
            {error}
          </motion.p>
        )}
      </motion.div>
    );
  }
);

Textarea.displayName = 'Textarea';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  description?: string;
  checked?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, className = '', checked = false, onChange, ...props }, ref) => {
    return (
      <motion.label 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start gap-2.5 cursor-pointer group"
      >
        <div className="relative mt-0.5">
          <input
            ref={ref}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className={`
              peer w-4 h-4 rounded border-2 border-gray-300 bg-gray-50
              checked:bg-gradient-to-br checked:from-indigo-500 checked:to-purple-600 checked:border-transparent
              focus:ring-2 focus:ring-indigo-500/20 focus:outline-none
              transition-all duration-200 cursor-pointer
              ${className}
            `}
            {...props}
          />
          <svg 
            className="absolute top-0.5 left-0.5 w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <span className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 transition-colors">
            {label}
          </span>
          {description && (
            <p className="text-xs text-gray-400 mt-0.5">{description}</p>
          )}
        </div>
      </motion.label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
